import {
    Box,
    Button,
    Card,
    Divider,
    Flex,
    Group,
    Menu,
    ScrollArea,
    Switch,
    Text,
    Loader, Tooltip,
    CloseButton, Modal, Space
} from "@mantine/core";
import AccordionSection from "@/PageComponents/AccordionSection";
import {
    IconBookDownload,
    IconSend, IconInfoCircle
} from "@tabler/icons-react";
import React, { FC, forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useForm } from "@mantine/form";
import * as Enums from "@/utils/enums";
import Constants from "@/utils/constants";
import BillingService from "@/services/billing-service";
import PS from "@/services/permission/permission-service";
import SubscriptionContext from "@/utils/subscription-context";
import { useMutation, useQuery } from "@tanstack/react-query";
import Helper from "@/utils/helper";
import { useDidUpdate, useDisclosure } from "@mantine/hooks";
import RecipientSelector, { Recipient } from "@/PageComponents/Message/Communication/RecipientSelector";
import Fetch from "@/utils/Fetch";
import Router from "next/router";
import { showNotification } from "@mantine/notifications";
import TemplateService from "@/services/template-service";
import ConfirmAction from "@/components/modals/confirm-action";
import ScRichTextEditorV2, {
    DEFAULT_SMS_MIN_HEIGHT,
} from "@/PageComponents/Message/Communication/ScRichTextEditorV2";
import { ReplacementTagInfo } from "@/PageComponents/Message/Communication/ReplacementTagInfo";
import MessageAttachments from "@/PageComponents/Message/Communication/MessageAttachments";

const smsMaxChar = 425 // 425 temporarily to account for replacement tags - actual 459 (for 3 multipart segments)
const MAX_CONTENT_SIZE = 1048576 / 2; // 500 KB in bytes

const EMPTY_TAGS: any[] = [];

const mapLegacyTypeToEnum = {
    'sms': Enums.MessageType.SMS,
    'email': Enums.MessageType.Email,
    'both': Enums.MessageType.Both,
}

interface RecipientValidationModalProps {
    opened: boolean;
    onClose: () => void;
    onConfirm: () => void;
    hasEmailWarning: boolean;
    hasSmsWarning: boolean;
}

const RecipientValidationModal: FC<RecipientValidationModalProps> = ({ opened, onClose, onConfirm, hasEmailWarning, hasSmsWarning }) => {
    const getTitle = () => {
        if (hasEmailWarning && hasSmsWarning) return "Some recipients won't receive this message";
        if (hasEmailWarning) return "Some recipients won't receive this email";
        if (hasSmsWarning) return "Some recipients won't receive this SMS";
        return "Some recipients won't receive this message";
    };

    const getMessage = () => {
        if (hasEmailWarning && hasSmsWarning) {
            return "Some of the selected recipients are not configured to receive email and/or SMS messages. They'll be skipped when you send.";
        }
        if (hasEmailWarning) {
            return "Some contacts aren't set up to receive email. They'll be skipped when you send.";
        }
        if (hasSmsWarning) {
            return "Some contacts aren't set up to receive SMSs. They'll be skipped when you send.";
        }
        return "";
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={<Text fw={700} size="lg">{getTitle()}</Text>}
            size="md"
            radius="md"
            padding="xl"
            centered
        >
            <Text size="sm" mb="xl">
                {getMessage()}
            </Text>
            <Flex justify="flex-end" gap="sm">
                <Button variant="outline" onClick={onClose} color="gray" px="xl">
                    Cancel
                </Button>
                <Button onClick={onConfirm} color="scBlue" px="xl">
                    Send anyway
                </Button>
            </Flex>
        </Modal>
    );
};

const getInitialDocument = (props: any) => {
    const dv: any[] = []
    if (props.attachQuote) {
        dv.push('quote')
    }
    if (props.attachInvoice) {
        dv.push('invoice')
    }
    if (props.attachPurchaseOrder) {
        dv.push('purchase_order')
    }
    if (props.attachJobCard) {
        dv.push('job_card')
    }
    return dv
}

export interface Contact {
    Title: null | string,
    FirstName: string,
    LastName: string,
    FullName: string,
    EmailAddress: string,
    MobileNumber: string,
    DesignationID: null | string,
    DesignationDescription: null | string,
    WorkNumber: null | string,
    HomeNumber: null | string,
    FaxNumber: null | string,
    IDNumber: null | string,
    Birthdate: null | Date,
    SendEmail: boolean,
    SendSMS: boolean,
    Unsubscribe: boolean,
    IsPrimary: boolean,
    IsPrimaryAccount: boolean,
    LocationID: null | string,
    LocationDescription: null | string,
    Location: null | string,
    CustomerID: string,
    CustomerName: string,
    Customer: null | string,
    ID: string,
    IsActive: boolean,
    CreatedBy: string,
    CreatedDate: string,
    ModifiedBy: string,
    ModifiedDate: string,
    RowVersion: string
}

export interface Employee {
    FirstName: string,
    LastName: string,
    EmailAddress: string,
    MobileNumber: string,
    UserID: string,
    UserName?: string,
    Password?: string,
    AuthUserIsActive: boolean,
    SendEmail: boolean,
    SendSMS: boolean,
    SendNotifications: boolean,
    IsAllJobCardStatus: boolean,
    IsAllStore: boolean,
    IsAllReport: boolean,
    PageSize: number,
    IsSalesRepresentative: boolean,
    IsTechnician: boolean,
    Owner: boolean,
    DisplayColor: string,
    FullName: string,
    OwnerControlled: boolean,
    SessionTimeOut: number,
    LastKnownLatitude?: number,
    LastKnownLongitude?: number,
    LastKnownActiveDate?: Date,
    PrimaryRole?: string,
    Permissions: any[],
    Roles: any[],
    Stores: any[],
    JobStatuses: any[],
    Reports: any[],
    ID: string,
    IsActive: boolean,
    CreatedBy: string,
    CreatedDate: string,
    ModifiedBy: string,
    ModifiedDate: string,
    RowVersion: string,
}

interface Template {
    Name: string;
    Module: number;
    TemplateType: number;
    Subject: string;
    EmailBody: string;
    SMSBody: string;
    MasterID: string;
    ID: string;
    IsActive: boolean;
    CreatedBy: string;
    CreatedDate: string;
    ModifiedBy: string;
    ModifiedDate: string;
    RowVersion: string;
}

export interface NewCommunicationFormProps {
    validateAndCloseCounter?: number;
    supplier: { Contacts: Contact[], [key: string]: any };
    customer: { Contacts: Contact[], [key: string]: any };
    item: any;
    itemId: string; //: id;
    itemUrl?: string;
    onClose?: () => void;
    onSent?: () => void;
    method: 0 | 1 | 2 | 'email' | 'sms' | 'both';
    moduleCode: string | number;
    templates: Template[]; //: templates.Results;
    attachQuote?: boolean;
    attachInvoice?: boolean;
    attachPurchaseOrder?: boolean;
    attachJobCard?: boolean;
    template?: Template;
    employees: Employee[]; //: employees.Results;
    documentDefinitionMetaData: any; //: documentDefinitionMetaData
    preSelectEmployeeId?: string;
    initialValues?:NewCommunicationFormInitialValues;
    autoFocus?: boolean;
    withBorder?: boolean;
    hideHeader?: boolean;
    emailMaxHeight?: number;
    onLoading?: (loading: boolean) => void;
    onMessageTypeChange?: (messageType: number) => void;
}

export interface NewCommunicationFormInitialValues{
    EmailBody?:string,
    Subject?:string
}

const NewMessageForm = forwardRef<any, NewCommunicationFormProps>(({ stickyHeaderOffset = 0, autoFocus = false, withBorder = true, hideHeader = false, emailMaxHeight = 600, ...props }: any, ref) => {

    const [accessStatus, setAccessStatus] = useState<any>(Enums.AccessStatus.None);
    const [selectedTemplateName, setSelectedTemplateName] = useState<string | null>(props.template?.Name || null);
    const [isInitialized, setIsInitialized] = useState(false);

    const subscriptionContext = useContext<any>(SubscriptionContext);
    const [credits, setCredits] = useState(-1)
    const [canBuyCredits, setCanBuyCredits] = useState(false)

    const {data: subscription} = useQuery(['subscriptionInfo'], () => BillingService.getSubcriptionInfo(), {
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
        refetchOnWindowFocus: false, // Don't refetch on tab focus
        refetchOnMount: false // Don't refetch on component remount
    })

    useEffect(() => {
        if (subscription) {
            const [subscriptionInfo] = subscription as [any, string];
            if (subscriptionInfo) {
                setAccessStatus(subscriptionInfo.AccessStatus);
                setCredits(subscriptionInfo.SMSCreditsPurchased - (subscriptionInfo.SMSCreditsUsed ?? 0))
                /**set purchase credits access**/
                let canAccess = (subscriptionInfo.AccessStatus === Enums.AccessStatus.Live || subscriptionInfo.AccessStatus === Enums.AccessStatus.Trial);
                let billingAccount = subscriptionInfo.BillingAccount;
                if (billingAccount && billingAccount.BillingProvider === (Enums.BillingProvider as any).Custom) {
                    canAccess = true;
                }
                let canBuy = PS.hasPermission(Enums.PermissionName.Subscriptions) && canAccess && subscriptionInfo.SMSRateExVAT > 0
                setCanBuyCredits(canBuy)
            }
        }
    }, [subscription])

    useEffect(() => {
        if (props.template?.Name) {
            setSelectedTemplateName(props.template.Name);
        }
    }, [props.template]);

    const validateRecipients = (recipients: Recipient[], messageType?: number) => {
        let result: {
            recipientsError: string | null,
            contactWarning: string | null,
            employeeWarning: string | null,
            isValid: boolean,
            hasEmailWarning: boolean,
            hasSmsWarning: boolean
        } = {
            isValid: true,
            recipientsError: null,
            contactWarning: null,
            employeeWarning: null,
            hasEmailWarning: false,
            hasSmsWarning: false
        };

        const contacts = recipients.filter(r => r.type === 'contact');
        const employees = recipients.filter(r => r.type === 'employee');

        // no recipients selected
        if (recipients.length === 0) {
            result.recipientsError = "At least one contact or employee must be selected";
            result.isValid = false;
        }

        const mType = messageType !== undefined ? messageType : +form.values.messageType;
        let sendingEmail = mType === Enums.MessageType.Email || mType === Enums.MessageType.Both;
        let sendingSMS = mType === Enums.MessageType.SMS || mType === Enums.MessageType.Both;

        if (result.isValid) {
            let contactCount = contacts.length;
            let contactEmailCount = contacts.filter((x: any) => x.EmailAddress && x.SendEmail).length;
            let contactSMSCount = contacts.filter((x: any) => x.MobileNumber && x.SendSMS).length;
            let contactEmailValid = sendingEmail && contactEmailCount > 0;
            let contactEmailIncomplete = sendingEmail && contactCount > contactEmailCount;
            let contactSMSValid = sendingSMS && contactSMSCount > 0;
            let contactSMSIncomplete = sendingSMS && contactCount > contactSMSCount;
            let contactValid = contactEmailValid || contactSMSValid;

            let employeeCount = employees.length;
            let employeeEmailCount = employees.filter((x: any) => x.EmailAddress && x.SendEmail).length;
            let employeeSMSCount = employees.filter((x: any) => x.MobileNumber && x.SendSMS).length;
            let employeeEmailValid = sendingEmail && employeeEmailCount > 0;
            let employeeEmailIncomplete = sendingEmail && employeeCount > employeeEmailCount;
            let employeeSMSValid = sendingSMS && employeeSMSCount > 0;
            let employeeSMSIncomplete = sendingSMS && employeeCount > employeeSMSCount;
            let employeeValid = employeeEmailValid || employeeSMSValid;

            // At least one recipient must be valid for the chosen channel
            const anyEmailValid = (sendingEmail && (contactEmailCount > 0 || employeeEmailCount > 0)) || !sendingEmail;
            const anySMSValid = (sendingSMS && (contactSMSCount > 0 || employeeSMSCount > 0)) || !sendingSMS;

            result.isValid = anyEmailValid && anySMSValid;

            if (contactCount > 0) {
                if (contactEmailIncomplete) result.hasEmailWarning = true;
                if (contactSMSIncomplete) result.hasSmsWarning = true;

                if (contactEmailIncomplete && contactSMSIncomplete) result.contactWarning = "Some contacts are not configured to receive emails and/or SMS messages";
                else if (contactEmailIncomplete) result.contactWarning = "Some contacts are not configured to receive emails";
                else if (contactSMSIncomplete) result.contactWarning = "Some contacts are not configured to receive SMS messages";
            }

            if (employeeCount > 0) {
                if (employeeEmailIncomplete) result.hasEmailWarning = true;
                if (employeeSMSIncomplete) result.hasSmsWarning = true;

                if (employeeEmailIncomplete && employeeSMSIncomplete) result.employeeWarning = "Some employees are not configured to receive emails and/or SMS messages";
                else if (employeeEmailIncomplete) result.employeeWarning = "Some employees are not configured to receive emails";
                else if (employeeSMSIncomplete) result.employeeWarning = "Some employees are not configured to receive SMS messages";
            }

            if (!result.isValid) {
                if (sendingEmail && sendingSMS) {
                    result.recipientsError = "No selected recipients are able to receive both email and SMS communications";
                } else if (sendingEmail) {
                    result.recipientsError = "No selected recipients are able to receive email communications";
                } else if (sendingSMS) {
                    result.recipientsError = "No selected recipients are able to receive SMS communications";
                }
            }
        }

        return result;
    };

    const form = useForm<{
        module: number
        itemId: string
        storeId: string
        messageType: number;
        recipients: Recipient[];
        subject: string;
        smsBody: string;
        emailBody: string;
        moduleAttachments: any[];
        localAttachments: any[];
        forms: any[];
        documents: any[];
    }>({
        initialValues: {
            module: +props.moduleCode,
            itemId: props.itemId,
            storeId: props.item.StoreID,
            messageType: typeof props.method === "string" ?
                mapLegacyTypeToEnum[props.method] ?? Enums.MessageType.Email :
                props.method ?? Enums.MessageType.Email,
            recipients: [],
            subject: props.template?.Subject || props.initialValues?.Subject || '', // relevant
            smsBody: props.template?.SMSBody || '',
            emailBody: props.template?.EmailBody || props.initialValues?.EmailBody || '',
            moduleAttachments: [],
            localAttachments: [],
            forms: [],
            documents: getInitialDocument(props) ?? []
        },
        // validateInputOnBlur: true,
        validateInputOnChange: true,
        validate: {
            subject: (val: any, values: any) => {
                const restrictedTags = TemplateService.testForValidSubject(val)
                if (restrictedTags.length !== 0) {
                    return 'Unfortunately ' + restrictedTags.join(', ') + ' is not allowed in the message subject'
                }
                if (val && val.length > 255) {
                    return 'Subject must be 255 characters or less';
                }
                const isEmail = +values.messageType === Enums.MessageType.Email || +values.messageType === Enums.MessageType.Both;
                if (isEmail && (!val || val.trim().length === 0)) {
                    return 'Subject is required when sending emails';
                }
                return null;
            },
            smsBody: (val: any, values: any) => {
                if (+values.messageType === Enums.MessageType.SMS || +values.messageType === Enums.MessageType.Both) {
                    if (!val || val.trim().length === 0) {
                        return 'Please enter an SMS message';
                    }
                    if (val.length > smsMaxChar) {
                        return `SMS content exceeds the maximum allowed characters of ${smsMaxChar}`;
                    }
                }
                return null;
            },
            recipients: (val: any, values: any) => {
                let validationResult = validateRecipients(val, +values.messageType);
                return validationResult.recipientsError;
            },
            emailBody: (val: any, values: any) => {
                if (+values.messageType === Enums.MessageType.Email || +values.messageType === Enums.MessageType.Both) {
                    const hasImage = /<img\b/i.test(val || '');
                    const effectivelyEmpty = !val || (val.replace(/<[^>]*>/g, '').trim().length === 0 && !val.includes('{{') && !hasImage);
                    if (effectivelyEmpty) {
                        return 'Email content is required';
                    }
                    const size = new Blob([val]).size;
                    if (size > MAX_CONTENT_SIZE) {
                        const excessSize = size - MAX_CONTENT_SIZE;
                        let excessSizeMessage = '';
                        if (excessSize < 1024) excessSizeMessage = `${excessSize} bytes`;
                        else if (excessSize < 1048576) excessSizeMessage = `${(excessSize / 1024).toFixed(2)} KB`;
                        else excessSizeMessage = `${(excessSize / 1048576).toFixed(2)} MB`;
                        return `Total content exceeds the 500KB limit by ${excessSizeMessage}`;
                    }
                }
                return null;
            }
        },
        transformValues: (values: any) => {
            const contacts = values.recipients.filter((r: Recipient) => r.type === 'contact');
            const employees = values.recipients.filter((r: Recipient) => r.type === 'employee');

            return {
                ...values,
                messageRecipientList: [
                    ...contacts.map((x: any) => (
                        {
                            'RecipientID': x.ID,
                            UserType: !!props.customer ? Enums.UserType.Customer : Enums.UserType.Supplier,
                            'SendEmail': x.SendEmail,
                            'SendSMS': x.SendSMS
                        }
                    )),
                    ...employees.map((x: any) => (
                        {
                            'RecipientID': x.ID,
                            UserType: Enums.UserType.Employee,
                            'SendEmail': x.SendEmail,
                            'SendSMS': x.SendSMS
                        }
                    )),
                ],
                messageType: +values.messageType,
                attachments: [...values.moduleAttachments, ...values.localAttachments],
                formHeaders: values.forms,
                attachQuote: values.documents.includes('quote'),
                attachInvoice: values.documents.includes('invoice'),
                attachJobCard: values.documents.includes('job_card'),
                attachWorkShop: values.documents.includes('workshop'),
                attachSignOff: values.documents.includes('sign_off'),
                attachJobSheet: values.documents.includes('job_sheet'),
                attachPurchaseOrder: values.documents.includes('purchase_order'),
            };
        }
    })


    const [confirmOptions, setConfirmOptions] = useState(Helper.initialiseConfirmOptions());


    const messageMutation = useMutation(
        ['messageMutation'],
        async (params: any) => {
            await Fetch.post({
                url: '/Message',
                params
            } as any).then(
                (messageRes: any) => {
                    if (messageRes && messageRes.HttpStatusCode == 200) {
                        Helper.mixpanelTrack(Constants.mixPanelEvents.createCommunication, {
                            "itemID": props.itemId
                        } as any);
                        return messageRes
                    } else {
                        throw new Error(messageRes.serverMessage || messageRes.message || 'Message could not be sent')
                    }
                }
            )

        }
        ,
        {
            onSuccess: (messageRes: any) => {
                showNotification({
                    id: 'messageSent',
                    message: 'Message added to queue',
                    color: 'scBlue',
                    autoClose: 3000
                })
                if (props.onSent) {
                    props.onSent()
                }
                if (props.onClose) {
                    props.onClose()
                } else {
                    // cleaning form dirty state as we have successfully submitted, and waiting a bit for state to update (this is necessary due to timing on page navigation)
                    Helper.formResetDirty(form);
                    Helper.waitABit().then(() => {
                        let url = props.itemUrl?.toLowerCase();
                        if (+props.moduleCode === Enums.Module.Asset) {
                            Helper.nextRouter(Router.push, '/asset/' + props.itemId + "?tab=communication");
                        } else if (+props.moduleCode === Enums.Module.PurchaseOrder) {
                            Helper.nextRouter(Router.push, '/purchase/' + props.itemId + "?tab=communication");
                        } else {
                            Helper.nextRouter(Router.push, url + props.itemId + "?tab=communication");
                        }
                    });
                }

            },
            onError: (err: Error) => {
                if (err.message.includes("(429)")) {
                    showNotification({
                        id: 'messageSent',
                        title: 'Message could not be sent',
                        message: "You have reached your limit sending messages, try again in a short while",
                        color: 'yellow.7',
                        autoClose: 3000
                    })
                } else {
                    showNotification({
                        id: 'messageSent',
                        title: 'Message could not be sent',
                        message: err.message || '',
                        color: 'yellow.7',
                        autoClose: 3000
                    })
                }
            }
        }
    );

    const submitButtonRef = useRef<HTMLButtonElement>(null);

    useImperativeHandle(ref, () => ({
        submit: () => {
            submitButtonRef.current?.click();
        },
        isLoading: messageMutation.isLoading,
        isDirty: form.isDirty()
    }));
    const skipWarningCheckRef = useRef(false);
    const [recipientModalOpened, {open: openRecipientModal, close: closeRecipientModal}] = useDisclosure(false);


    Helper.preventRouteChange(
        isInitialized && form.isDirty(),
        form.resetDirty,
        (co: any) => setConfirmOptions({
            ...co,
            showCancel: false,
            discardButtonText: 'Discard',
            confirmButtonText: 'Continue editing',
            heading: "Leave this page?",
            text: "You have unfinished changes",
        }),
        () => { /*no op - continue editing */}
    );
    useDidUpdate(() => {
        if (props.validateAndCloseCounter) {
            if (isInitialized && form.isDirty()) {
                setConfirmOptions({
                    ...Helper.initialiseConfirmOptions(),
                    display: true,
                    showCancel: false,
                    showDiscard: true,
                    onDiscard: () => {
                        props.onClose && props.onClose()
                    },
                    heading: "Discard this message?",
                    text: `You have unfinished changes`,
                    confirmButtonText: "Continue editing",
                    onConfirm: () => {/* no op */}
                })
            } else {
                props.onClose && props.onClose()
            }
        }
    }, [props.validateAndCloseCounter]);


    useEffect(() => {
        props.onLoading && props.onLoading(messageMutation.isLoading)
    }, [messageMutation.isLoading])

    useEffect(() => {
        props.onMessageTypeChange && props.onMessageTypeChange(+form.values.messageType)
    }, [form.values.messageType])

    const {data: replacementTags = EMPTY_TAGS} = useQuery(['replacementTags'], async () => {
        const {Results: tags, ...res} = await Fetch.get({
            url: `/Template/GetReplacementTagList?module=${props.moduleCode}&searchPhrase=${''}`
        })
        if (tags) {
            return tags as any[]
        } else {
            throw new Error(res.serverMessage || res.message || 'something went wrong')
        }
    })

    const subjectReplacementTags = useMemo(() => {
        if (!replacementTags) return EMPTY_TAGS;
        return replacementTags.filter(tag => {
            const invalidTags = TemplateService.testForValidSubject(`{{${tag.Name}}}`);
            return invalidTags.length === 0;
        });
    }, [replacementTags]);


    const handleSubmit = (formVals: any) => {
        if (!skipWarningCheckRef.current && (validatedRecipients.contactWarning || validatedRecipients.employeeWarning)) {
            openRecipientModal();
            return;
        }

        messageMutation.mutate(formVals);
        skipWarningCheckRef.current = false;
    }

    const handleErrors = (formErrors: any) => {
        // Handle errors
        if (formErrors) {
            const firstErrorField = Object.keys(formErrors)[0];
            let errorElement = (formErrors.recipients) ? (
                document.querySelector(`#recipients`)
            ) : form.getInputNode(firstErrorField);
            errorElement?.scrollIntoView({behavior: 'smooth', block: 'end'});

            showNotification({
                message: "There are errors on the page",
                color: "yellow",
            });
        }
    }

    const validatedRecipients = useMemo(() => {
        return validateRecipients(form.values.recipients, +form.values.messageType);
    }, [form.values.recipients, form.values.messageType]);

    const attachmentAccordionHeader = (
        <Text fw={700} size="16px">Email attachments</Text>
    );

    const emailActive = +form.values.messageType === Enums.MessageType.Email || +form.values.messageType === Enums.MessageType.Both;
    const smsActive = +form.values.messageType === Enums.MessageType.SMS || +form.values.messageType === Enums.MessageType.Both;

    const buttonText = useMemo(() => {
        if (emailActive && smsActive) return "Send Email and SMS";
        if (emailActive) return "Send Email";
        if (smsActive) return "Send SMS";
        return "Send";
    }, [emailActive, smsActive]);

    const channelDisabled = props.template?.ID === Constants.templateIDs.TemplateCustomerZone;

    const setChannels = (email: boolean, sms: boolean) => {
        // Prevent both channels being disabled
        if (!email && !sms) return;
        const next = email && sms
            ? Enums.MessageType.Both
            : (email ? Enums.MessageType.Email : Enums.MessageType.SMS);
        form.setFieldValue('messageType', next);
        setTimeout(() => form.validateField('recipients'), 0);
    };

    const channelToggleStyle = (active: boolean, isOnlyActive: boolean) => ({
        border: `1px solid var(--mantine-color-${active ? 'scBlue-6' : 'gray-3'})`,
        borderRadius: 6,
        padding: '8px 10px',
        background: 'white',
        cursor: (channelDisabled || (active && isOnlyActive)) ? 'not-allowed' : 'pointer',
        opacity: channelDisabled ? 0.6 : 1,
    });

    return <>
        <ConfirmAction options={confirmOptions} setOptions={setConfirmOptions}/>
        <RecipientValidationModal
            opened={recipientModalOpened}
            onClose={() => {
                skipWarningCheckRef.current = false;
                closeRecipientModal();
            }}
            onConfirm={() => {
                skipWarningCheckRef.current = true;
                closeRecipientModal();
                submitButtonRef.current?.click();
            }}
            hasEmailWarning={validatedRecipients.hasEmailWarning}
            hasSmsWarning={validatedRecipients.hasSmsWarning}
        />

        <Card withBorder={withBorder} radius={'6px'} p={0} style={{ border: hideHeader ? 'none' : undefined }}>
            <form
                onSubmit={form.onSubmit(handleSubmit, handleErrors)}
            >
                {hideHeader && <button type="submit" ref={submitButtonRef} style={{ display: 'none' }} />}
                {!hideHeader && (
                    <Flex
                        align={'center'}
                        px={'lg'}
                        py={7}
                        bg={'gray.0'}
                        pos={'sticky'}
                        top={stickyHeaderOffset}
                        style={{borderBottom: '1px solid var(--mantine-color-gray-3)'}}
                    >
                        <Text fw={700} size={'md'} c={'dark.7'}>Compose Message</Text>
                        <Group ml={'auto'} gap={'sm'}>
                            <Button
                                ref={submitButtonRef}
                                type={'submit'}
                                leftSection={messageMutation.isLoading ? <Loader color={'scBlue'} size={18}/> : <IconSend/>}
                                disabled={messageMutation.isLoading}
                            >
                                {buttonText}
                            </Button>
                            {props.onClose && (
                                <CloseButton
                                    onClick={props.onClose}
                                    size={'md'}
                                />
                            )}
                        </Group>
                    </Flex>
                )}
                <Box pt={'xs'} pb={'lg'}>
                    <Box px={'lg'}>
                        <Flex direction={'column'} pb={{base: 'xs', xl: 10}}>
                            <Flex
                                align={'center'}
                                gap={'md'}
                                wrap={'wrap'}
                            >
                                <Group gap={'xs'} align={'center'}>
                                    <Text size={'sm'} c={'dimmed'}>Channel</Text>
                                    <Box
                                        style={channelToggleStyle(emailActive, !smsActive)}
                                        onClick={() => !channelDisabled && setChannels(!emailActive, smsActive)}
                                        role={'button'}
                                        aria-pressed={emailActive}
                                    >
                                        <Group gap={8} wrap={'nowrap'}>
                                            <Text size={'sm'} c={emailActive ? 'scBlue.6' : 'dark.7'} fw={500}>Send
                                                Email</Text>
                                            <Switch
                                                size={'sm'}
                                                checked={emailActive}
                                                disabled={channelDisabled}
                                                style={{pointerEvents: 'none'}}
                                                readOnly
                                            />
                                        </Group>
                                    </Box>
                                    <Box
                                        style={channelToggleStyle(smsActive, !emailActive)}
                                        onClick={() => !channelDisabled && setChannels(emailActive, !smsActive)}
                                        role={'button'}
                                        aria-pressed={smsActive}
                                    >
                                        <Group gap={8} wrap={'nowrap'}>
                                            <Text size={'sm'} c={smsActive ? 'scBlue.6' : 'dark.7'} fw={500}>Send SMS</Text>
                                            <Switch
                                                size={'sm'}
                                                checked={smsActive}
                                                disabled={channelDisabled}
                                                style={{pointerEvents: 'none'}}
                                                readOnly
                                            />
                                        </Group>
                                    </Box>
                                </Group>

                                <Divider orientation="vertical" my={'auto'} mx={'sm'} h={30} visibleFrom="sm"/>

                                <Menu shadow="md" width={200}
                                      trapFocus={false}
                                      position={'bottom-start'}
                                      disabled={!!selectedTemplateName}
                                >
                                    <Menu.Target>
                                        <Button
                                            variant={selectedTemplateName ? 'light' : 'outline'}
                                            styles={{
                                                root: {
                                                    borderColor: 'var(--mantine-color-gray-4)'
                                                }
                                            }}
                                            color="gray"
                                            c={'gray.6'}
                                            rightSection={
                                                selectedTemplateName ? (
                                                    <CloseButton
                                                        size="sm"
                                                        variant="transparent"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedTemplateName(null);
                                                        }}
                                                    />
                                                ) : (
                                                    <IconBookDownload
                                                        color={'var(--mantine-color-dark-7)'}
                                                        stroke={1.4}
                                                        size={22}
                                                    />
                                                )
                                            }
                                        >
                                            {selectedTemplateName ? ("Template: " + selectedTemplateName) : "Insert template"}
                                        </Button>
                                    </Menu.Target>
                                    <Menu.Dropdown>
                                        {
                                            props.templates.length === 0 ? <Menu.Label>No templates available</Menu.Label> :
                                                <ScrollArea.Autosize mah={'50vh'} type={'auto'}>
                                                    {
                                                        props.templates.map(
                                                            (x: any) => <Menu.Item
                                                                key={x.ID}
                                                                onClick={() => {
                                                                    form.setFieldValue('subject', x.Subject || '')
                                                                    form.setFieldValue('smsBody', x.SMSBody || '')
                                                                    form.setFieldValue('emailBody', x.EmailBody || '')
                                                                    setSelectedTemplateName(x.Name);
                                                                }}
                                                            >
                                                                {x.Name}
                                                            </Menu.Item>
                                                        )
                                                    }
                                                </ScrollArea.Autosize>
                                        }
                                    </Menu.Dropdown>
                                </Menu>

                            </Flex>
                        </Flex>

                        {/*<Divider mx="-lg" />*/}
                        <Flex gap={'sm'} direction={{base: 'column', md: 'row'}} wrap={'wrap'}>
                            <Flex flex={1} mb={'sm'} direction={'column'} py={{base: 0, xl: 10}}>
                                <Text size={'sm'} c={'dimmed'} mb={'xs'}>Select recipients</Text>
                                <Box
                                    id={'recipients'}
                                >
                                    <RecipientSelector
                                        moduleCode={props.moduleCode}
                                        itemId={props.itemId}
                                        messageType={+form.values.messageType}
                                        preSelectEmployeeId={props.preSelectEmployeeId}
                                        {...form.getInputProps('recipients')}
                                        onInitialized={() => {
                                            form.resetDirty();
                                            setIsInitialized(true);
                                        }}
                                    />
                                </Box>
                            </Flex>
                            <Box flex={1} maw={500} mb={{base: 'sm', md: 'auto'}} mt={{base: '-sm', md: 'auto'}} ml={{base: 0, xl: 'auto'}}>
                                <ReplacementTagInfo replacementTags={replacementTags}/>
                            </Box>
                        </Flex>

                        <Flex direction={'column'}>

                            {/* Email + SMS two-column responsive area */}
                            <Flex
                                gap={{base: 'lg', md: 72}}
                                wrap={'wrap'}
                                align={'stretch'}
                                mb={'lg'}
                            >
                                {emailActive && (
                                    <Box style={{
                                        flexGrow: 1,
                                        flexBasis: smsActive ? 360 : '100%',
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }} w={smsActive ? {base: '100%', md: 'calc(60% - 8px)'} : '100%'}>
                                        <Card
                                            withBorder
                                            radius={'6px'}
                                            p={0}
                                            style={{
                                                flexGrow: 1,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                borderColor: form.errors.emailBody ? 'var(--mantine-color-yellow-6)' : undefined,
                                            }}
                                            w={'100%'}
                                        >
                                            <Box p="lg" pb="xs">
                                                <Text fw={600} c={'dark.7'}>Email</Text>
                                            </Box>

                                            <Box px="lg">
                                                <ScRichTextEditorV2
                                                    mode="subject"
                                                    placeholder="Subject"
                                                    replacementTags={subjectReplacementTags}
                                                    allReplacementTags={replacementTags}
                                                    {...form.getInputProps('subject')}
                                                    autoFocus={autoFocus && emailActive}
                                                    bg="gray.1"
                                                    unstyled
                                                />
                                            </Box>

                                            <Box px="lg" style={{
                                                flex: 1,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                minHeight: 200
                                            }}>
                                                <ScRichTextEditorV2
                                                    mode="email"
                                                    {...form.getInputProps('emailBody')}
                                                    replacementTags={replacementTags}
                                                    hideError={true}
                                                    stickyOffset={stickyHeaderOffset + 68}
                                                    showHtmlEditButton
                                                    flex={1}
                                                    maxHeight={emailMaxHeight}
                                                />
                                            </Box>
                                        </Card>
                                        {form.errors.emailBody && (
                                            <Text mt={5} size="11px" c="yellow.6">
                                                {form.errors.emailBody as string}
                                            </Text>
                                        )}
                                    </Box>
                                )}

                                {smsActive && (
                                    <Box style={{
                                        flexGrow: 1,
                                        flexBasis: emailActive ? 280 : '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        maxWidth: 450,
                                        margin: '0 auto',
                                        width: emailActive ? 'calc(40% - 8px)' : '100%'
                                    }}>
                                        <Card
                                            withBorder
                                            radius={'6px'}
                                            p={0}
                                            style={{
                                                flexGrow: 1,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                borderColor: form.errors.smsBody ? 'var(--mantine-color-yellow-6)' : undefined,
                                            }}
                                            w={'100%'}
                                        >
                                            <Flex justify="space-between" align="center" p="lg" pb="xs">
                                                <Flex align="center">
                                                    <Text fw={600} c={'dark.7'}>SMS</Text>
                                                    <Tooltip
                                                        maw={380}
                                                        multiline
                                                        label="SMS messages consume 1-3 credits depending on length. Note: Replacement tags can make messages longer, potentially causing truncation."
                                                        withArrow position="top" color={'scBlue'}>
                                                        <Flex justify={'center'} miw={40} style={{cursor: 'help'}}>
                                                            <IconInfoCircle size="1.2rem"
                                                                            color="var(--mantine-color-scBlue-6)"/>
                                                        </Flex>
                                                    </Tooltip>
                                                </Flex>

                                                <Flex align="center" gap={'xs'}>
                                                    <Text fw={400} size={'sm'} c={'gray.7'}>SMS balance:</Text>
                                                    <Text fw={600} size={'sm'} c={'dark.7'}>{credits}</Text>
                                                </Flex>

                                                {/*<Flex align="center" gap={'xs'}>
                                                    <Text size={'sm'} fw={500} style={{ color: form.values.smsBody?.length > smsMaxChar ? 'var(--mantine-color-yellow-7)' : 'inherit' }}>
                                                        {form.values.smsBody?.length || 0} / {smsMaxChar}
                                                    </Text>
                                                </Flex>*/}
                                            </Flex>

                                            <Divider mx={'lg'}/>

                                            <Box px="lg" style={{
                                                flex: 1,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                containerType: 'size',
                                                minHeight: DEFAULT_SMS_MIN_HEIGHT
                                            }}>
                                                <ScRichTextEditorV2
                                                    mode="sms"
                                                    placeholder="Type your message here.."
                                                    replacementTags={replacementTags}
                                                    {...form.getInputProps('smsBody')}
                                                    hideError={true}
                                                    autoFocus={autoFocus && !emailActive && smsActive}
                                                    flex={1}
                                                    maxHeight="100cqh"
                                                />
                                            </Box>
                                        </Card>
                                        {form.errors.smsBody && (
                                            <Text mt={5} size="11px" c="yellow.6">
                                                {form.errors.smsBody as string}
                                            </Text>
                                        )}
                                    </Box>
                                )}
                            </Flex>

                            {/*<Divider mx="-lg" />*/}

                            {/* Attachments in collapsible accordion */}
                            {emailActive && (
                                <Card
                                    withBorder
                                    radius={'6px'}
                                    p={0}
                                    // mx={'-lg'}
                                >
                                    <AccordionSection
                                        label={attachmentAccordionHeader}
                                        initiallyOpen
                                        styles={{
                                            control: {padding: '0 16px'},
                                            label: {padding: '18px 12px'},
                                            content: {padding: '4px 14px 12px'}
                                        }}
                                    >
                                        <Box px={'16px'} pb={'md'}>
                                            <MessageAttachments
                                                {...props}
                                                onFormsChanged={(x: any) => {
                                                    form.setFieldValue('forms', x)
                                                }}
                                                onAttachmentsChanged={(x: any) => {
                                                    form.setFieldValue('moduleAttachments', x)
                                                }}
                                                onLocalAttachmentsChanged={(x: any) => {
                                                    form.setFieldValue('localAttachments', x)
                                                }}
                                                onDocumentsChanged={(x: any) => {
                                                    form.setFieldValue('documents', x)
                                                }}
                                                defaultDocs={form.values.documents}
                                                selectedDocs={form.values.documents}
                                                selectedForms={form.values.forms.map((f: any) => f.ID)}
                                                selectedAttachments={form.values.moduleAttachments.map((a: any) => a.ID)}
                                                selectedLocalFiles={form.values.localAttachments}
                                            />
                                        </Box>
                                    </AccordionSection>
                                </Card>
                            )}

                            <Space h={40}/>


                        </Flex>


                    </Box>
                </Box>
            </form>
        </Card>

    </>;
});

export default NewMessageForm